import { z } from 'zod';

import { axiosRubyClient } from '@/util/axios/axiosClient';

// GRAVY-10898 — preferred pool auto-invite (PD-1.x) and the client's
// "unfilled 2 hours before start" email, as graphy's /api/admin endpoints send
// them (PoolDispatch::Builder, Customers::Builder).
//
// Every write is TENANT_ADMIN-role only: helpdesk may GET, but PATCH / POST
// return 403. Responses are parsed leniently (nullish, passthrough) so an
// older server that leaves a field out still renders instead of erroring.

const id = z.number().int();
const count = z.number().int().nonnegative();
const iso = z.string().nullish();
// A flag the API leaves out or sends as null is read as off, never on.
const flag = z
  .boolean()
  .nullish()
  .transform((v) => v === true);

export const poolAutoInviteSchema = z
  .object({
    customer_id: id,
    enabled: flag,
    // Only jobs posted at or after this moment are auto-invited.
    enabled_at: iso,
    wave_size: z.number().int().nullish(),
    default_wave_size: z.string().nullish(),
    max_wave_size: z.number().int().nullish(),
    // The global kill switch. Unknown is treated as on so the screen does not
    // claim the switch is off when an older server simply did not say.
    automation_on: z
      .boolean()
      .nullish()
      .transform((v) => v !== false),
  })
  .passthrough();

export const unfilledNoticeSchema = z
  .object({ customer_id: id, enabled: flag })
  .passthrough();

export const ladderStep = z.enum([
  'pending',
  'pool',
  'widened',
  'alerted',
  'skipped',
]);
export type LadderStep = z.infer<typeof ladderStep>;

const waveSchema = z
  .object({
    wave_number: z.number().int(),
    trigger: z.string().nullish(),
    invited_count: count.nullish().transform((v) => v ?? 0),
    invited_worker_ids: z.array(id).nullish(),
    excluded_counts: z.record(z.number()).nullish(),
    sent_at: iso,
  })
  .passthrough();

export const poolDispatchSchema = z
  .object({
    job_id: id,
    auto_invite_enabled: flag,
    applies: flag,
    // An unknown step from a newer server reads as pending, not as an error.
    ladder_step: z
      .string()
      .nullish()
      .transform((v): LadderStep => {
        const parsed = ladderStep.safeParse(v);
        return parsed.success ? parsed.data : 'pending';
      }),
    skipped_reason: z.string().nullish(),
    paused: flag,
    paused_at: iso,
    paused_by_user_id: z.number().int().nullish(),
    exhausted_at: iso,
    widened_at: iso,
    alerted_2h_at: iso,
    waves: z
      .array(waveSchema)
      .nullish()
      .transform((v) => v ?? []),
  })
  .passthrough();

const eligibleSchema = z
  .object({
    rank: z.number().int().nullish(),
    worker_id: id,
    name: z.string().nullish(),
    distance_mi: z.number().nullish(),
    tier: z.string().nullish(),
    client_shifts: z.number().nullish(),
    score: z.number().nullish(),
  })
  .passthrough();

const excludedSchema = z
  .object({
    worker_id: id,
    name: z.string().nullish(),
    reason: z.string(),
    reason_label: z.string().nullish(),
    distance_mi: z.number().nullish(),
  })
  .passthrough();

export const poolEligibilitySchema = z
  .object({
    job_id: id,
    radius_miles: z.number().nullish(),
    job_located: z.boolean().nullish(),
    members_count: count.nullish(),
    eligible: z
      .array(eligibleSchema)
      .nullish()
      .transform((v) => v ?? []),
    excluded: z
      .array(excludedSchema)
      .nullish()
      .transform((v) => v ?? []),
  })
  .passthrough();

export type PoolAutoInvite = z.infer<typeof poolAutoInviteSchema>;
export type UnfilledNotice = z.infer<typeof unfilledNoticeSchema>;
export type PoolDispatch = z.infer<typeof poolDispatchSchema>;
export type PoolWave = z.infer<typeof waveSchema>;
export type PoolEligibility = z.infer<typeof poolEligibilitySchema>;
export type PoolEligible = z.infer<typeof eligibleSchema>;
export type PoolExcluded = z.infer<typeof excludedSchema>;

const envelope = <T extends z.ZodTypeAny>(data: T) =>
  z.object({ data }).passthrough();

const customerPath = (customerId: number | string, leaf: string) =>
  `/api/admin/customers/${customerId}/${leaf}`;
const jobPath = (jobId: number | string, leaf = '') =>
  `/api/admin/jobs/${jobId}/pool_dispatch${leaf}`;

export const poolAutomationClient = {
  async getPoolAutoInvite(customerId: number | string) {
    const { data } = await axiosRubyClient.get(
      customerPath(customerId, 'pool_auto_invite'),
    );
    return envelope(poolAutoInviteSchema).parse(data).data;
  },

  // Only `enabled` is sent: a PATCH without `wave_size` leaves the client's
  // wave size as it is (PoolDispatch::SettingsService#parse).
  async setPoolAutoInvite(customerId: number | string, enabled: boolean) {
    const { data } = await axiosRubyClient.patch(
      customerPath(customerId, 'pool_auto_invite'),
      { enabled },
    );
    return envelope(poolAutoInviteSchema).parse(data).data;
  },

  async getUnfilledNotice(customerId: number | string) {
    const { data } = await axiosRubyClient.get(
      customerPath(customerId, 'unfilled_client_notice'),
    );
    return envelope(unfilledNoticeSchema).parse(data).data;
  },

  async setUnfilledNotice(customerId: number | string, enabled: boolean) {
    const { data } = await axiosRubyClient.patch(
      customerPath(customerId, 'unfilled_client_notice'),
      { enabled },
    );
    return envelope(unfilledNoticeSchema).parse(data).data;
  },

  async getDispatch(jobId: number | string) {
    const { data } = await axiosRubyClient.get(jobPath(jobId));
    return envelope(poolDispatchSchema).parse(data).data;
  },

  async pause(jobId: number | string) {
    const { data } = await axiosRubyClient.post(jobPath(jobId, '/pause'));
    return envelope(poolDispatchSchema).parse(data).data;
  },

  async resume(jobId: number | string) {
    const { data } = await axiosRubyClient.post(jobPath(jobId, '/resume'));
    return envelope(poolDispatchSchema).parse(data).data;
  },

  async getEligibility(jobId: number | string) {
    const { data } = await axiosRubyClient.get(jobPath(jobId, '/eligibility'));
    return envelope(poolEligibilitySchema).parse(data).data;
  },
};
